import { useState } from "react";
import { MoreHorizontal, Share2, Edit, Trash2, Flag, Bookmark, Copy, FolderPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Prompt } from "@/lib/types";
import { toast } from "sonner";
import { AddToCollectionModal } from "@/components/Collections";

interface PromptActionsDropdownProps {
  prompt: Prompt;
  onEdit: () => void;
  onDelete: () => void;
  onSave: () => void;
  onReport: () => void;
  isSaved?: boolean;
}

export function PromptActionsDropdown({
  prompt,
  onEdit,
  onDelete,
  onSave,
  onReport,
  isSaved = false
}: PromptActionsDropdownProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  
  const isOwner = user?.id === prompt.userId;
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/prompt/${prompt.id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!");
      setOpen(false);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      toast.success("Prompt copied to clipboard!");
      setOpen(false);
    } catch (error) {
      toast.error("Failed to copy prompt");
    }
  };

  const handleAction = (action: () => void) => {
    action();
    setOpen(false);
  };

  return (
    <>
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 opacity-60 hover:opacity-100 transition-opacity data-[state=open]:opacity-100"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        side="bottom"
        sideOffset={5}
        className="w-48 bg-popover border border-border shadow-lg z-[9999]"
      >
        <DropdownMenuItem 
          onClick={handleCopy}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Copy className="h-4 w-4" />
          Copy Prompt
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={handleShare}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Share2 className="h-4 w-4" />
          Share Prompt
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleAction(onSave)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Bookmark className="h-4 w-4" />
          {isSaved ? "Unsave" : "Save"} Prompt
        </DropdownMenuItem>
        
        {user && (
          <DropdownMenuItem 
            onClick={() => {
              setOpen(false);
              setCollectionModalOpen(true);
            }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <FolderPlus className="h-4 w-4" />
            Add to Collection
          </DropdownMenuItem>
        )}
        
        {(isOwner || isAdmin) && (
          <>
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => handleAction(onEdit)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Edit className="h-4 w-4" />
              Edit Prompt
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={() => handleAction(onDelete)}
              className="flex items-center gap-2 cursor-pointer text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete Prompt
            </DropdownMenuItem>
          </>
        )}
        
        {!isOwner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => handleAction(onReport)}
              className="flex items-center gap-2 cursor-pointer text-orange-500 hover:text-orange-600"
            >
              <Flag className="h-4 w-4" />
              Report Prompt
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    
    {user && (
      <AddToCollectionModal
        open={collectionModalOpen}
        onOpenChange={setCollectionModalOpen}
        promptId={prompt.id}
      />
    )}
    </>
  );
}